import React, { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import dayjs from 'dayjs';
import { BrandLoveData } from '../hooks/useBrandLoveData';
import { fetchBrandColors, getBrandColors } from '../utils/brandColors';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useFilterContext } from '../context/FilterContext';
import { SectionCard, SectionHeader, NestedPanel } from './ui/Section';
import { RankBarChart } from './ui/charts';

interface RankData {
  name: string;
  value: number;
}

interface BrandLoveRankingProps {
  data: BrandLoveData | null;
  selectedBrands: string[];
  startDate: string | null;
  endDate: string | null;
}

/**
 * Computes an average across numeric values (ignoring null/undefined)
 */
function average(values: Array<number | null | undefined>): number {
  const validValues = values.filter((v): v is number => v != null);
  if (validValues.length === 0) return 0;
  const sum = validValues.reduce((acc, v) => acc + v, 0);
  return sum / validValues.length;
}

function BrandLoveRanking({ data, selectedBrands = [], startDate, endDate }: BrandLoveRankingProps) {
  const { allBrands } = useFilterContext();
  const axiosClient = useAxiosClient();
  // All hooks must be called at the top level
  const [view, setView] = useState('average_brand_love');
  
  // Brand colors state
  const [brandColors, setBrandColors] = useState<{ [key: string]: string }>({});
  const [colorsLoading, setColorsLoading] = useState(true);
  const [colorRefreshKey, setColorRefreshKey] = useState(0);

  // Load brand colors
  useEffect(() => {
    const loadBrandColors = async () => {
      if (!data) return;
      
      try {
        setColorsLoading(true);
        const brandColorConfig = await fetchBrandColors(axiosClient);
        const colors = await getBrandColors(axiosClient, data.blove_chart.brands, allBrands, brandColorConfig);
        setBrandColors(colors as { [key: string]: string });
      } catch (error) {
        console.error('Error loading brand colors:', error);
        // Fallback to original color scheme
        const fallbackColors: { [key: string]: string } = {};
        if (data?.blove_chart?.brands) {
          data.blove_chart.brands.forEach((brand, index) => {
            fallbackColors[brand] = `hsl(${index * 45}, 70%, 50%)`;
          });
        }
        setBrandColors(fallbackColors);
      } finally {
        setColorsLoading(false);
      }
    };

    loadBrandColors();
  }, [data, allBrands, axiosClient, colorRefreshKey]);
  
  // Listen for brand color updates via custom event
  useEffect(() => {
    const handleBrandColorsUpdated = () => {
      // Only increment refresh key if we actually have data to refresh
      if (data?.blove_chart?.brands) {
        setColorRefreshKey(prev => prev + 1);
      }
    };

    window.addEventListener('brandColorsUpdated', handleBrandColorsUpdated);
    return () => {
      window.removeEventListener('brandColorsUpdated', handleBrandColorsUpdated);
    };
  }, [data]);

  // Extract and memoize chart data (safely handling null data)
  const chartData = useMemo(() => {
    if (!data) return { brands: [], dates: [], values: [], postCounts: [], yearlyMetrics: {}, dateRangeMetrics: {} };
    return {
      brands: data.blove_chart.brands,
      dates: data.blove_chart.dates,
      values: data.blove_chart.values,
      postCounts: data.post_count_chart?.values || [],
      yearlyMetrics: data.yearly_metrics || {},
      dateRangeMetrics: data.date_range_metrics || {}
    };
  }, [data]);

  // Memoize filtered date indexes based on the date filter
  const filteredDateIndexes = useMemo(() => {
    if (!data) return [];
    const { dates } = chartData;
    const result: number[] = [];
    
    for (let i = 0; i < dates.length; i++) {
      const currentDate = dayjs(dates[i]);
      const isAfterStart = !startDate || currentDate.isAfter(dayjs(startDate)) || currentDate.isSame(dayjs(startDate));
      const isBeforeEnd = !endDate || currentDate.isBefore(dayjs(endDate)) || currentDate.isSame(dayjs(endDate));
      
      if (isAfterStart && isBeforeEnd) {
        result.push(i);
      }
    }
    
    return result.length > 0 ? result : dates.map((_, idx) => idx);
  }, [chartData, startDate, endDate]);

  // Get date indexes for the current year to date (YTD)
  const annualIndexes = useMemo(() => {
    if (!data) return [];
    const { dates } = chartData;
    const CURRENT_YEAR_START = dayjs().startOf('year');
    
    return dates
      .map((dateStr, idx) => {
        const date = dayjs(dateStr);
        // Include only dates from the current year
        return date.isAfter(CURRENT_YEAR_START) || date.isSame(CURRENT_YEAR_START) ? idx : -1;
      })
      .filter((idx) => idx !== -1);
  }, [chartData]);

  // Calculate average brand love for a brand using actual blove_chart data
  const calculateAverageBrandLove = useMemo(() => {
    if (!data) return () => 0;
    const { values } = chartData;
    
    return (brandIndex: number, dateIndexes: number[]): number => {
      if (dateIndexes.length === 0) return 0;
      
      // Get all brand love values for this brand in the selected date range
      const brandLoveValues = dateIndexes
        .map(dateIdx => values[brandIndex][dateIdx])
        .filter(value => value !== null && value !== undefined);
      
      // Return 0 if no valid values
      if (brandLoveValues.length === 0) return 0;
      
      // Calculate average
      return brandLoveValues.reduce((sum, value) => sum + value, 0) / brandLoveValues.length;
    };
  }, [chartData]);

  // Function to calculate post volume for a brand using actual post count data
  const calculatePostVolume = useMemo(() => {
    if (!data) return () => 0;
    const { postCounts } = chartData;
    
    return (brandIndex: number, dateIndexes: number[]): number => {
      // If no post count data is available, return 0
      if (!postCounts || !postCounts[brandIndex] || dateIndexes.length === 0) return 0;
      
      // Sum up the post counts for the selected dates
      const totalPosts = dateIndexes.reduce((total, dateIdx) => {
        const count = postCounts[brandIndex][dateIdx];
        // Only add if it's a valid number
        return total + (count !== null && count !== undefined ? count : 0);
      }, 0);
      
      return totalPosts;
    };
  }, [chartData]);

  // Function to create rank data for YTD (using backend metrics)
  const createYearlyRankData = useMemo(() => {
    if (!data) return [] as RankData[];
    const { brands, yearlyMetrics } = chartData;
    
    return brands
      .filter((brand) => 
        selectedBrands.length === 0 || selectedBrands.includes(brand)
      )
      .map((brand) => {
        const brandIndex = brands.indexOf(brand);
        const metrics = yearlyMetrics[brand] || { avg_blove: 0, avg_engagement: 0, post_volume: 0 };
        let metricValue = 0;
        
        switch (view) {
          case 'average_brand_love':
            metricValue = metrics.avg_blove;
            break;
          case 'average_engagement':
            metricValue = metrics.avg_engagement;
            break;
          case 'post_volume':
            metricValue = metrics.post_volume;
            break;
          default:
            metricValue = metrics.avg_blove;
            break;
        }

        return {
          name: brand,
          value: metricValue
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [chartData, selectedBrands, view]);

  // Function to create rank data for date range (using backend metrics)
  const createDateRangeRankData = useMemo(() => {
    if (!data) return [] as RankData[];
    const { brands, dateRangeMetrics } = chartData;
    
    return brands
      .filter((brand) => 
        selectedBrands.length === 0 || selectedBrands.includes(brand)
      )
      .map((brand) => {
        const brandIndex = brands.indexOf(brand);
        const metrics = dateRangeMetrics[brand] || { avg_blove: 0, avg_engagement: 0, post_volume: 0 };
        let metricValue = 0;
        
        switch (view) {
          case 'average_brand_love':
            metricValue = metrics.avg_blove;
            break;
          case 'average_engagement':
            metricValue = metrics.avg_engagement;
            break;
          case 'post_volume':
            metricValue = metrics.post_volume;
            break;
          default:
            metricValue = metrics.avg_blove;
            break;
        }

        return {
          name: brand,
          value: metricValue
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [chartData, selectedBrands, view]);

  // Handle toggles
  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: string
  ) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  // Early return if data is null
  if (!data) {
    return null;
  }

  // Function to format values based on the current view
  const formatValue = (value: number) => {
    if (view === 'post_volume') {
      return Math.round(value).toLocaleString(); // Format as integer
    } else {
      return Math.round(value).toLocaleString(); // Format as integer for brand love and engagement
    }
  };

  // Create appropriate labels based on the view
  const getValueLabel = () => {
    switch(view) {
      case 'average_engagement':
        return 'Avg. Engagement';
      case 'post_volume':
        return 'Post Count';
      default:
        return 'Brand Love Score';
    }
  };

  const valueLabel = getValueLabel();

  const renderRanking = (title: string, rows: RankData[]) => (
    <NestedPanel sx={{ flex: '1 1 420px' }}>
      <SectionHeader title={title} level="sub" />
      <RankBarChart
        data={rows.map((r) => ({ ...r, color: brandColors[r.name] }))}
        formatValue={formatValue}
        valueLabel={valueLabel}
      />
    </NestedPanel>
  );

  return (
    <SectionCard>
      <SectionHeader
        title="Brand Love Ranking"
        info="This card displays brand rankings across three metrics: Average Brand Love scores each brand's audience affinity (averaged by day), Average Engagement measures average engagement score per post, and Post Volume counts total posts published. Use the toggle buttons to switch between these 3 metrics. The first chart ranks the currently-filtered date range, and the second is YTD (Year to Date)."
        actions={
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            aria-label="brand love view"
            size="small"
          >
            <ToggleButton value="average_brand_love">Average Brand Love</ToggleButton>
            <ToggleButton value="average_engagement">Average Engagement</ToggleButton>
            <ToggleButton value="post_volume">Post Volume</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {renderRanking('Selected date range', createDateRangeRankData)}
        {renderRanking(`${dayjs().year()} year to date`, createYearlyRankData)}
      </Box>
    </SectionCard>
  );
}

export default BrandLoveRanking; 